/**
 * Vue Analytics Plugin
 *
 * Provides:
 * - $analytics global property for Options API
 * - useAnalytics() composable for Composition API
 * - v-track-click directive for declarative click tracking
 * - Automatic page view tracking on route changes (if Vue Router is used)
 */

import { initAnalytics, getAnalytics } from '../services/analyticsTracking.js';

// Symbol for injection key
const analyticsKey = Symbol('analytics');

/**
 * Vue Analytics Plugin
 */
export const AnalyticsPlugin = {
    install(app, options = {}) {
        // Initialize analytics
        const analytics = initAnalytics(options);

        // Provide for Composition API
        app.provide(analyticsKey, analytics);

        // Add global property for Options API
        app.config.globalProperties.$analytics = analytics;

        // Register v-track-click directive
        app.directive('track-click', {
            mounted(el, binding) {
                const handler = () => {
                    const eventName = binding.value?.name || binding.value || el.textContent?.trim() || 'button_click';
                    const data = binding.value?.data || {};

                    analytics.trackClick(eventName, {
                        element_tag: el.tagName.toLowerCase(),
                        element_text: el.textContent?.trim()?.substring(0, 100),
                        element_class: el.className,
                        ...data,
                    });
                };

                el._analyticsClickHandler = handler;
                el.addEventListener('click', handler);
            },
            unmounted(el) {
                if (el._analyticsClickHandler) {
                    el.removeEventListener('click', el._analyticsClickHandler);
                    delete el._analyticsClickHandler;
                }
            },
        });

        // Register v-track-view directive (tracks when element comes into view)
        app.directive('track-view', {
            mounted(el, binding) {
                if (!('IntersectionObserver' in window)) return;

                const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            const eventName = binding.value?.name || binding.value || 'element_viewed';
                            const data = binding.value?.data || {};

                            analytics.track(eventName, {
                                element_id: el.id || null,
                                element_class: el.className,
                                ...data,
                            });

                            // Only track once unless modifier is used
                            if (!binding.modifiers.repeat) {
                                observer.unobserve(el);
                            }
                        }
                    });
                }, {
                    threshold: binding.modifiers.full ? 1.0 : 0.5,
                });

                el._analyticsObserver = observer;
                observer.observe(el);
            },
            unmounted(el) {
                if (el._analyticsObserver) {
                    el._analyticsObserver.disconnect();
                    delete el._analyticsObserver;
                }
            },
        });

        // Track route changes if Vue Router is available
        if (app.config.globalProperties.$router) {
            app.config.globalProperties.$router.afterEach((to, from) => {
                // Small delay to ensure page title is updated
                setTimeout(() => {
                    analytics.trackPageView(to.meta?.title || document.title, {
                        route_name: to.name,
                        route_path: to.path,
                        from_path: from.path,
                    });
                }, 100);
            });
        }
    },
};

/**
 * Composable for Composition API
 * @returns {object} Analytics methods
 */
export function useAnalytics() {
    const analytics = getAnalytics();

    return {
        /**
         * Track a custom event
         */
        track: analytics.track.bind(analytics),

        /**
         * Track a page view
         */
        trackPageView: analytics.trackPageView.bind(analytics),

        /**
         * Track a click event
         */
        trackClick: analytics.trackClick.bind(analytics),

        /**
         * Track a modal open/close
         */
        trackModal: analytics.trackModal.bind(analytics),

        /**
         * Track a search
         */
        trackSearch: analytics.trackSearch.bind(analytics),

        /**
         * Track a filter being applied
         */
        trackFilter: analytics.trackFilter.bind(analytics),

        /**
         * Track a feature usage
         */
        trackFeature: analytics.trackFeature.bind(analytics),

        /**
         * Track an error
         */
        trackError: analytics.trackError.bind(analytics),

        /**
         * Track API response time
         */
        trackApiTime: analytics.trackApiTime.bind(analytics),

        /**
         * Manually flush the event queue
         */
        flush: analytics.flush.bind(analytics),
    };
}

export default AnalyticsPlugin;
